import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { useUserInfo } from '@/hooks'
import { Toast, Popup } from 'react-vant'
import { useSendCode } from '@/hooks/useSendCode'
import Ripple from '@/components/Ripple'
import CustomButton from '@/components/CustomButton'
import { VerActionType, type VerActionItem, type SelectVerificationProps } from './index.type'

/**
 * 验证方式选择组件
 * 用于选择邮箱、手机号或谷歌验证器进行身份验证
 */
const SelectVerification: React.FC<SelectVerificationProps> = ({
  scene,
  onSuccess,
  showTitle = true,
  title,
  showPhoneOption = false,
  className = ''
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { userInfo } = useUserInfo()
  const { sendCode, sendCodeLoading } = useSendCode()
  const [showBindGoogleDialog, setShowBindGoogleDialog] = useState(false)

  // 未绑定谷歌验证器时，自动弹出风险提示弹窗
  useEffect(() => {
    if (userInfo && !userInfo.is_bind_google) {
      setShowBindGoogleDialog(true)
    }
  }, [userInfo])

  // 根据用户信息动态生成验证选项列表
  const verActionItems = useMemo<VerActionItem[]>(() => {
    const items: VerActionItem[] = []

    // 只有当 showPhoneOption 为 true 且用户有手机号时才显示手机验证选项
    // if (showPhoneOption && userInfo?.phone) {
    //   items.push({
    //     icon: '/icon/phone-icon.svg',
    //     title: t('手机号'),
    //     description: t('通过手机号验证码验证'),
    //     actionType: VerActionType.PhoneVer
    //   })
    // }

    // 如果用户绑定了谷歌验证器，放在第一位并标注推荐
    if (userInfo?.is_bind_google) {
      items.push({
        icon: '/icon/google-ver.svg',
        title: t('谷歌验证器'),
        description: t('通过谷歌验证器进行验证'),
        actionType: VerActionType.GoogleVer
      })
    }

    // 只有当用户有邮箱时才显示邮箱验证选项
    if (userInfo?.email) {
      items.push({
        icon: '/icon/email-ver.svg',
        title: t('邮箱'),
        description: t('通过绑定邮箱进行验证'),
        actionType: VerActionType.EmailVer
      })
    }

    return items
  }, [userInfo?.phone, userInfo?.email, userInfo?.is_bind_google, showPhoneOption, t])

  // 处理选择验证方式
  const handleSelectVer = useCallback(async (actionType: VerActionType) => {
    if (actionType === VerActionType.PhoneVer || actionType === VerActionType.EmailVer) {
      if (sendCodeLoading) return

      Toast.loading(t('正在发送验证码'))
      const account = actionType === VerActionType.PhoneVer
        ? userInfo?.phone || ''
        : userInfo?.email || ''

      // 先发送验证码，成功后再回调
      const data = await sendCode({
        account,
        scene
      })

      if (data) {
        onSuccess({
          actionType,
          account
        })
      }
    } else {
      // 谷歌验证器不需要发送验证码，直接回调
      onSuccess({
        actionType,
        account: ''
      })
    }
  }, [sendCodeLoading, userInfo?.phone, userInfo?.email, sendCode, scene, onSuccess, t])

  // 跳转到绑定谷歌验证器页面（通过弹窗进入时传递来源路径及原始 state，绑定成功后跳回原页面并恢复参数）
  const handleGoBindGoogle = useCallback(() => {
    setShowBindGoogleDialog(false)
    navigate('/safety/bind-google', {
      state: {
        from: location.pathname + location.search,
        fromState: location.state // 保留原页面的路由 state，防止回跳后参数丢失
      }
    })
  }, [navigate, location.pathname, location.search, location.state])

  return (
    <div className={className}>
      {showTitle && (
        <div className="text-7 font-medium text-black">
          {title || t('选择验证方式')}
        </div>
      )}
      <div className={showTitle ? 'mt-6 space-y-4' : 'space-y-4'}>
        {verActionItems.map((item, index) => {
          // 第一项且是谷歌验证器时标注推荐
          const isRecommended = index === 0 && item.actionType === VerActionType.GoogleVer
          return (
            <div key={item.actionType} className="relative">
              {isRecommended && (
                <span className="absolute top-0 left-4 z-10 bg-[var(--primary-color)] text-white text-xs px-2 py-0.5 rounded-b-1">
                  {t('推荐')}
                </span>
              )}
              <Ripple className="rounded-2xl w-full">
                <div
                  className="flex justify-between items-center py-5 px-4 border border-solid border-[#EAECF0] rounded-2xl cursor-pointer transition-all"
                  onClick={() => handleSelectVer(item.actionType)}
                >
                  <div className="flex items-center gap-2">
                    <img src={item.icon} alt={item.title} className="w-5 h-5" />
                    <div className="text-base text-black">{item.description}</div>
                  </div>
                  <img className="w-2" src="/icon/right-info.svg" alt="" />
                </div>
              </Ripple>
            </div>
          )
        })}
      </div>

      {/* 未绑定谷歌验证器的风险提示底部弹窗 */}
      <Popup
        visible={showBindGoogleDialog}
        onClose={() => setShowBindGoogleDialog(false)}
        position="bottom"
        round
        closeable
        closeIcon={<img src="/icon/close.svg" alt="" />}
        closeIconPosition="top-right"
      >
        <div className="px-5 pt-4 pb-12 flex flex-col items-center">
          <div className="text-lg font-medium text-black self-start">{t('风险提示')}</div>
          <div className="w-24 h-24 my-6">
            <img src="/icon/google-ver-tips-icon.svg" alt="" className="w-full h-full object-contain" />
          </div>
          <div className="text-base text-[#667085] text-center leading-6">
            <Trans i18nKey="bindGoogleTip" components={{ bold: <span className="font-bold text-black" /> }}>
              为保障您的账户资产安全，请先绑定<span className="font-bold text-black">谷歌验证器</span>，完成安全验证后再继续操作。
            </Trans>
          </div>
          <div className="w-full mt-8">
            <CustomButton onClick={handleGoBindGoogle}>
              {t('立即绑定')}
            </CustomButton>
          </div>
        </div>
      </Popup>
    </div>
  )
}

export default React.memo(SelectVerification)

// 导出类型供外部使用
export { VerActionType } from './index.type'
export type { VerActionItem, SelectVerificationProps } from './index.type'
export type { SendCodeScene } from '@/hooks/useSendCode'
